import './converge-form-table.scss';

import { Box, TableFooter, TablePagination } from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';

import { FormViewValueField } from './form-view-value-field';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { config } from '@converge/config';
import { styled } from '@mui/material/styles';
import { useState } from 'react';

export const StyledConvergeFormTableCell = styled(TableCell)(() => ({
  padding: '10px',
  // backgroundColor: 'rgba(255, 0, 0, 0.05)',
  fontSize: 14,
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#FBFBFB',
    color: '#000000',
    fontWeight: '700',
  },
  '.MuiGrid-item': {
    maxWidth: '100%',
    padding: '0',
  },
  '&:first-of-type': {
    paddingRight: '30px',
  },
}));

export const StyledConvergeFormTableRow = styled(TableRow)(() => ({
  '&:last-of-type td': {
    border: 0,
    paddingBottom: '12px',
  },
  '&:first-of-type td': {
    paddingTop: '15px',
  },
}));

export const StyledConvergeFormTableContainer = styled(TableContainer)(() => ({
  border: '1px solid #E7E7E7',
  borderRadius: '4px',
}));

export interface IConvergeFormTableColumn<T> {
  id: string;
  label: string;
  align?: 'left' | 'right' | 'center' | 'justify' | undefined;
  width?: string;
  render?: (
    row: T,
    index?: number
  ) => JSX.Element | string | number | undefined;
  renderValue?: (value: unknown) => JSX.Element | string | number | undefined;
}

interface IConvergeFormTableProps<R> {
  columns: IConvergeFormTableColumn<R>[];
  rows: R[] | undefined;
  emptyRowMessage?: string | null;
  title?: string | null;
  baseFieldPath?: string;
  enablePagination?: boolean;
}

export const ConvergeFormTable = <R,>({
  columns,
  rows,
  emptyRowMessage,
  title,
  baseFieldPath,
  enablePagination,
}: IConvergeFormTableProps<R>) => {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const startIndex = currentPage * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRows = enablePagination
    ? rows?.slice(startIndex, endIndex)
    : rows;

  return (
    <Box className="converge-form-table">
      <div className="header">{title}</div>
      <StyledConvergeFormTableContainer>
        <Table>
          <TableHead className="converge-section-title">
            <StyledConvergeFormTableRow>
              {columns.map((column) => (
                <ConvergeFormTableHeaderCell {...column} key={column.id} />
              ))}
            </StyledConvergeFormTableRow>
          </TableHead>
          <TableBody>
            {paginatedRows?.map((row, index) => (
              <ConvergeFormTableRow
                row={row}
                columns={columns}
                key={index + startIndex}
                index={index + startIndex}
                baseFieldPath={baseFieldPath}
              />
            ))}
            {!rows?.length && (
              <StyledConvergeFormTableRow>
                <TableCell colSpan={columns.length}>
                  {emptyRowMessage ?? 'No result'}
                </TableCell>
              </StyledConvergeFormTableRow>
            )}
          </TableBody>
          {enablePagination && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={config.paginatedTableRowsPerPageOptions}
                  count={rows?.length ?? 0}
                  rowsPerPage={rowsPerPage}
                  page={currentPage}
                  onPageChange={(_event, newPage) => setCurrentPage(newPage)}
                  onRowsPerPageChange={(event) =>
                    setRowsPerPage(+event.target.value)
                  }
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </StyledConvergeFormTableContainer>
    </Box>
  );
};

const ConvergeFormTableHeaderCell = <T,>(
  column: IConvergeFormTableColumn<T>
) => {
  return (
    <StyledConvergeFormTableCell key={column.id}>
      {column.label}
    </StyledConvergeFormTableCell>
  );
};

interface IConvergeFormTableRowProps<T> {
  row: T;
  columns: IConvergeFormTableColumn<T>[];
  index: number;
  baseFieldPath?: string;
}

const ConvergeFormTableRow = <T,>({
  row,
  columns,
  index,
  baseFieldPath,
}: IConvergeFormTableRowProps<T>) => {
  return (
    <StyledConvergeFormTableRow>
      {columns.map((column) => (
        <StyledConvergeFormTableCell
          key={column.id}
          align={column.align}
          width={column.width}
        >
          {column.render ? (
            column.render(row, index)
          ) : (
            <ConvergeFormTableDefaultCell
              column={column}
              baseFieldPath={baseFieldPath}
              index={index}
              renderValue={column.renderValue}
            />
          )}
        </StyledConvergeFormTableCell>
      ))}
    </StyledConvergeFormTableRow>
  );
};

const ConvergeFormTableDefaultCell = <T,>({
  column,
  baseFieldPath,
  index,
  renderValue,
}: {
  column: IConvergeFormTableColumn<T>;
  baseFieldPath?: string;
  index?: number;
  renderValue?: (value: unknown) => JSX.Element | string | number | undefined;
}) => {
  return (
    <FormViewValueField
      name={`${baseFieldPath}.${index}.${column.id}`}
      renderValue={renderValue}
    />
  );
};
